// src/pages/ConfigSelectorPage.tsx
import React from "react";
import DataInputPage from "./dataInput/DataInputPage";
import { Tool } from "./dataInput/types";


interface Props {
  tool:          string;
  toolAppList:   { nm: string }[];
  onAppSelected: (appNm: string) => void;
  children?:     React.ReactNode;
}

type Mode = "previous" | "new";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cspa * { box-sizing: border-box; margin: 0; padding: 0; }
  .csp {
    font-family: 'Inter', sans-serif;
    background: #f3f4f6;
    min-height: 100vh;
    color: #1a1d23;
    padding: 32px;
  }
  .csp-nav-btn {
    all: unset; box-sizing: border-box;
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 10px 14px; border-radius: 8px;
    cursor: pointer; border: 1px solid transparent;
    font-family: 'Inter', sans-serif; font-size: 13.5px; font-weight: 500;
    color: #4b5563; transition: all 0.15s ease; background: transparent;
  }
  .csp-nav-btn:hover { background: #f9fafb; color: #111827; }
  .csp-nav-btn.active { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; font-weight: 600; }
  .csp-app-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 7px; cursor: pointer;
    border: 1px solid transparent; transition: all 0.12s; margin-bottom: 4px;
  }
  .csp-app-row:hover { background: #f9fafb; border-color: #e5e7eb; }
  .csp-app-row.sel { background: #eef2ff; border-color: #c7d2fe; }
  .csp-search {
    width: 100%; padding: 8px 12px 8px 34px;
    border: 1px solid #e2e6ea; border-radius: 7px;
    font-family: 'Inter', sans-serif; font-size: 13px;
    color: #1a1d23; background: #fff; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .csp-search:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
  .csp-search::placeholder { color: #9ca3af; }
  .csp-btn {
    all: unset; box-sizing: border-box;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; padding: 11px 16px; border-radius: 8px;
    background: #4f46e5; color: #fff;
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: background 0.15s, transform 0.12s;
  }
  .csp-btn:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); }
  .csp-btn:disabled { background: #e0e7ff; color: #a5b4fc; cursor: not-allowed; transform: none; }
  @keyframes csp-fade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
  .csp-fade { animation: csp-fade 0.18s ease; flex: 1; min-height: 0; display: flex; flex-direction: column; }
  .csp-fade > .gw-wizard,
  .csp-fade > .gw-wizard-embedded { flex: 1; min-height: 0; }

`;

const AppList: React.FC<{ apps:{nm:string}[]; selected:string; onSelect:(nm:string)=>void; }> = ({ apps, selected, onSelect }) => {
  const [q, setQ] = React.useState("");
  const list = apps.filter(a => a.nm.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div style={{ position:"relative", marginBottom:12 }}>
        <svg style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", width:15, height:15, color:"#9ca3af" }} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
        </svg>
        <input className="csp-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search applications…"/>
      </div>
      {!apps.length
        ? <div style={{ padding:"32px 0", textAlign:"center", color:"#9ca3af", fontSize:13 }}>No applications found.</div>
        : <div style={{ maxHeight:340, overflowY:"auto" }}>
            {list.map(app => {
              const sel = selected===app.nm;
              return (
                <div key={app.nm} className={`csp-app-row${sel?" sel":""}`} onClick={()=>onSelect(app.nm)}>
                  <div style={{ width:30, height:30, borderRadius:6, flexShrink:0, background:sel?"#eef2ff":"#f3f4f6", border:`1px solid ${sel?"#c7d2fe":"#e5e7eb"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:sel?"#4338ca":"#6b7280" }}>
                    {app.nm[0]?.toUpperCase()}
                  </div>
                  <span style={{ flex:1, fontSize:14, fontWeight:sel?600:400, color:sel?"#3730a3":"#374151" }}>{app.nm}</span>
                  {sel && <svg width="16" height="16" viewBox="0 0 20 20" fill="#4f46e5"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                </div>
              );
            })}
            {!list.length && <div style={{ padding:"20px 0", textAlign:"center", color:"#9ca3af", fontSize:13 }}>No results for "{q}"</div>}
          </div>
      }
    </div>
  );
};

const ConfigSelectorPage: React.FC<Props> = ({ tool, toolAppList, onAppSelected, children }) => {
  const [mode, setMode] = React.useState<Mode>("previous");
  const [sel, setSel]   = React.useState("");
  const isData = ["BUILD_DQ_USING_DATA","BUILD_DG_USING_DATA","BUILD_MDM_USING_DATA"].includes(tool);
  const toolLabels: Record<string,string> = {
    BUILD_DQ_USING_DATA:"DQ Rules & Governance", BUILD_DG_USING_DATA:"Data Governance",
    BUILD_MDM_USING_DATA:"Master Data Management", BUILD_MDM_USING_NLP:"MDM via NLP",
    MIGRATION_INFA_LEGACY_TO_IDMC:"Migration → IDMC", MIGRATION_INFA_LEGACY_TO_RELTIO:"Migration → Reltio",
  };

  // For new-discovery mode, the panel needs to fill so the wizard footer
  // sits at the bottom. For previous-config it can grow to its content.
  const panelMinHeight = mode === "new" ? 640 : undefined;

  return (
    <div className="csp">
      <style>{CSS}</style>
      <div style={{ maxWidth:1400, margin:"0 auto" }}>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:12, fontWeight:500, color:"#6b7280", fontFamily:"'JetBrains Mono',monospace", marginBottom:4 }}>
            {toolLabels[tool]||tool}
          </div>
          <h1 style={{ fontSize:20, fontWeight:700, color:"#111827" }}>Application Setup</h1>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"200px 1fr", gap:16, alignItems:"start" }}>
          {/* Sidebar */}
          <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:8, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ padding:"6px 10px 8px", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:0.5, textTransform:"uppercase" }}>Mode</div>
            {([{id:"previous" as Mode,label:"Previous Config",icon:"📂"},{id:"new" as Mode,label:"New Discovery",icon:"✦"}]).map(m=>(
              <button key={m.id} className={`csp-nav-btn${mode===m.id?" active":""}`} onClick={()=>setMode(m.id)}>
                <span style={{ fontSize:14 }}>{m.icon}</span><span>{m.label}</span>
              </button>
            ))}
            <div style={{ margin:"12px 0 8px", height:1, background:"#f3f4f6" }}/>
            <div style={{ padding:"4px 10px 8px" }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Available</div>
              <div style={{ fontSize:26, fontWeight:700, color:"#111827" }}>{toolAppList.length}</div>
              <div style={{ fontSize:12, color:"#6b7280" }}>configured apps</div>
            </div>
          </div>

          {/* Panel */}
          <div style={{
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: panelMinHeight,
          }}>
            <div style={{
              padding: "14px 24px",
              borderBottom: "1px solid #f3f4f6",
              background: "#fafafa",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}>
              <span style={{ fontSize:14 }}>{mode==="previous"?"📂":"✦"}</span>
              <span style={{ fontWeight:600, fontSize:15, color:"#111827" }}>
                {mode==="previous"?"Select Application":"New Discovery"}
              </span>
              <span style={{ marginLeft:"auto", fontSize:12, color:"#6b7280" }}>
                {mode==="previous"?`${toolAppList.length} available`:"AI pipeline"}
              </span>
            </div>
            <div style={{
              padding: "24px 28px",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}>
              {mode==="previous" && (
                <div className="csp-fade">
                  <p style={{ fontSize:13.5, color:"#6b7280", marginBottom:20, lineHeight:1.6 }}>
                    Select a previously configured application to continue.
                  </p>
                  <AppList apps={toolAppList} selected={sel} onSelect={setSel}/>
                  <div style={{ marginTop:20, paddingTop:20, borderTop:"1px solid #f3f4f6" }}>
                    <button className="csp-btn" disabled={!sel} onClick={()=>onAppSelected(sel)}>
                      {sel?`Continue with "${sel}"`:"Select an application above"}{sel&&<span>→</span>}
                    </button>
                  </div>
                </div>
              )}
              {mode==="new" && (
                <div className="csp-fade">
                  {isData
                    ? <DataInputPage tool={tool as Tool} layout="embedded" onPipelineComplete={onAppSelected}/>
                    : <div style={{padding:24}}>{children}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigSelectorPage;